import { useState } from "react";
import { Send, Mail, Globe, MapPin } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow, HeroLines } from "@/components/Reveal";
import { toast } from "sonner";

const TOPICS = [
  ["collector", "Collector Acquisition"],
  ["commission", "Commission Request"],
  ["wholesale", "Wholesale / Retail Buyer Inquiry"],
  ["gallery", "Gallery Representation"],
  ["press", "Press & Media"],
  ["general", "General Inquiry"],
];

const INFO = [
  { icon: Mail, label: "Email the studio", value: "r.kuensting@gmail.com" },
  { icon: Globe, label: "On the web", value: "artistsstudiogallery.com" },
  { icon: MapPin, label: "Shipping", value: "Worldwide, crated & insured" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "collector", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await API.post("/contact", form);
      setSent(true);
      toast.success("Message sent to the studio", { description: "Richard replies personally, usually within two days." });
    } catch (err) {
      toast.error(formatApiError(err, "The message could not be sent."));
    } finally {
      setSending(false);
    }
  };

  const field = "w-full bg-paper border border-ink/15 rounded-md px-4 py-3.5 text-sm text-ink placeholder:text-subtle focus:outline-none focus:border-terracotta/60 transition-colors";

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Reach Richard Kuensting's studio — collector inquiries, commissions, and gallery representation." />
      <header className="max-w-7xl mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-12">
        <Eyebrow>Contact the Studio</Eyebrow>
        <HeroLines lines={["Write to", "the studio."]}
          className="font-serif font-light text-ink text-4xl sm:text-6xl mt-4 leading-[1.05] tracking-tight" />
        <p className="text-stone2 mt-6 max-w-xl text-sm sm:text-base leading-relaxed">
          Every message lands on Richard&rsquo;s own desk — acquisitions, commissions, press, or simply to talk about a painting.
        </p>
      </header>

      <section className="max-w-7xl mx-auto px-5 sm:px-10 pb-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          {sent ? (
            <div className="bg-paper border border-ink/10 deckle p-10 sm:p-14 text-center" data-testid="contact-success">
              <p className="font-serif text-3xl sm:text-4xl text-ink leading-snug">Thank you — your note is on its way.</p>
              <p className="text-stone2 text-sm mt-4 max-w-md mx-auto leading-relaxed">
                A copy has gone to the studio desk. Expect a personal reply, usually within two days.
              </p>
              <button data-testid="contact-send-another" onClick={() => { setSent(false); setForm({ name: "", email: "", topic: "collector", message: "" }); }}
                className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-ink/20 rounded-full px-6 py-3 hover:bg-ink hover:text-linen transition-colors">
                Write another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-paper border border-ink/10 deckle p-6 sm:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input data-testid="contact-form-name" required placeholder="Your name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                <input data-testid="contact-form-email" required type="email" placeholder="Email address" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
              </div>
              <select data-testid="contact-form-topic" value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })} className={field}>
                {TOPICS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <textarea data-testid="contact-form-message" required rows={7}
                placeholder="Tell the studio what you have in mind — a work you love, a wall you're filling, a landscape you carry with you."
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={field} />
              <button data-testid="contact-form-submit" disabled={sending} type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-ink text-linen rounded-full py-4 text-xs font-mono uppercase tracking-[0.2em] hover:bg-terracotta transition-colors duration-300 disabled:opacity-60">
                <Send size={14} /> {sending ? "Sending…" : "Send to the Studio"}
              </button>
            </form>
          )}
        </Reveal>

        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="bg-night text-linen p-8 sm:p-10 deckle spotlight">
              <Eyebrow className="text-ochre">The Fine Print</Eyebrow>
              <ul className="mt-8 space-y-6">
                {INFO.map((i) => (
                  <li key={i.label} className="flex gap-4">
                    <i.icon size={18} className="text-ochre shrink-0 mt-0.5" />
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-linen/50">{i.label}</p>
                      <p className="text-sm mt-1">{i.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-linen/10 mt-8 pt-6">
                <p className="text-xs text-linen/60 leading-relaxed">
                  Commissions open seasonally. Current wait: one to two months.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
